import { useFormContext, useFormState, Path, PathValue } from "react-hook-form";
import { useEffect } from "react";

/**
 * Recalculates hours (visitCount × duration) only when **visit count** is dirty — not on pristine
 * load and not when only other fields changed. Uses `watch` for the value and `useFormState` +
 * `getFieldState` so we subscribe only to that field (no `void formState` hack).
 */
export function usePayrollVisitCountHoursCalculation<
  TFieldValues extends Record<string, unknown>,
>({
  visitCountPath,
  hoursPath,
  hoursBoundToVisits,
  mostFrequentPlannedVisitDuration,
}: {
  visitCountPath: Path<TFieldValues>;
  hoursPath: Path<TFieldValues>;
  hoursBoundToVisits?: boolean | null;
  mostFrequentPlannedVisitDuration?: string | null;
}) {
  const { watch, setValue, getValues, getFieldState, control } =
    useFormContext<TFieldValues>();
  const visitCount = watch(visitCountPath);

  const visitCountFormState = useFormState({
    control,
    name: visitCountPath,
    exact: true,
  });

  const isVisitCountDirty = getFieldState(
    visitCountPath,
    visitCountFormState,
  ).isDirty;

  useEffect(() => {
    if (!isVisitCountDirty) {
      return;
    }

    if (
      !hoursBoundToVisits ||
      !mostFrequentPlannedVisitDuration ||
      visitCount === null ||
      visitCount === undefined ||
      visitCount === ""
    ) {
      return;
    }

    const visitCountNum = Number(visitCount);
    const durationNum = Number(mostFrequentPlannedVisitDuration);

    if (isNaN(visitCountNum) || isNaN(durationNum)) {
      return;
    }

    const calculatedHours = (visitCountNum * durationNum).toFixed(2);
    const currentHours = getValues(hoursPath);

    if (currentHours !== calculatedHours) {
      setValue(
        hoursPath,
        calculatedHours as PathValue<TFieldValues, Path<TFieldValues>>,
        {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        },
      );
    }
  }, [
    visitCount,
    isVisitCountDirty,
    hoursBoundToVisits,
    mostFrequentPlannedVisitDuration,
    setValue,
    getValues,
    hoursPath,
  ]);
}
